import React, { useEffect, useRef, useState } from 'react';

/* ── Responsive hook ── */
function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const fn = () => setW(window.innerWidth);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return w;
}

/* ── Scroll-reveal hook ── */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

const services = [
  { icon: '✦', title: 'AI-Powered Apps',      desc: 'We build applications that learn, adapt, and improve continuously using cutting-edge AI and ML technologies.', color: '#2dd4bf' },
  { icon: '⬡', title: 'Custom Software',       desc: 'Tailored solutions engineered for startups, enterprises, and every stage of growth in between.',                color: '#818cf8' },
  { icon: '◈', title: 'Cloud & Automation',    desc: 'Harness cloud infrastructure and intelligent automation to streamline operations and boost productivity.',       color: '#fb923c' },
  { icon: '◎', title: 'Innovation Consulting', desc: 'We guide businesses in integrating AI and modern technologies into their products and workflows.',               color: '#34d399' },
];

const stats = [
  { value: '50+',  label: 'Projects Delivered'  },
  { value: '98%',  label: 'Client Satisfaction' },
  { value: '5+',   label: 'Years Experience'    },
  { value: '24/7', label: 'Support Available'   },
];

const ServiceCard = ({ service, delay }) => {
  const ref = useReveal();
  return (
    <div ref={ref} className="glass-card" style={{
      padding: '28px 24px', opacity: 0, transform: 'translateY(24px)',
      transition: `opacity 0.55s ease ${delay}ms, transform 0.55s ease ${delay}ms, background 0.25s, border-color 0.25s, box-shadow 0.25s`,
      display: 'flex', flexDirection: 'column', gap: 14,
    }}>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: `${service.color}18`, border: `1px solid ${service.color}35`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: service.color, fontSize: 20 }}>
        {service.icon}
      </div>
      <h3 style={{ fontSize: 16, fontWeight: 600, color: '#f0f4f8', letterSpacing: '-0.01em', margin: 0 }}>{service.title}</h3>
      <p  style={{ fontSize: 14, lineHeight: 1.7, color: '#8b9bb4', fontWeight: 300, margin: 0, flex: 1 }}>{service.desc}</p>
      <div style={{ height: 2, width: 36, borderRadius: 2, background: `linear-gradient(90deg,${service.color},transparent)`, marginTop: 'auto' }} />
    </div>
  );
};

const Home = () => {
  const w        = useWindowWidth();
  const isMobile = w <= 600;
  const isTablet = w <= 900;
  const px       = isMobile ? '18px' : isTablet ? '28px' : '40px';

  const heroRef  = useReveal();
  const statsRef = useReveal();

  return (
    <main className="page-enter" style={{ padding: '0 0 80px', maxWidth: 1160, margin: '0 auto', width: '100%' }}>

      {/* Hero */}
      <section ref={heroRef} style={{ padding: isMobile ? '56px 18px 36px' : `90px ${px} 60px`, display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 760, opacity: 0, transform: 'translateY(28px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}>
        <div className="section-label">Apps ViT Limited</div>
        <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: isMobile ? '30px' : 'clamp(36px,5vw,62px)', fontWeight: 700, lineHeight: 1.12, color: '#f0f4f8', letterSpacing: '-0.02em', margin: 0 }}>
          Building the future with{' '}
          <span style={{ background: 'linear-gradient(90deg,#2dd4bf,#818cf8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            intelligent software
          </span>
        </h1>
        <p style={{ fontSize: isMobile ? '15px' : '18px', lineHeight: 1.7, color: '#8b9bb4', maxWidth: 560, fontWeight: 300, margin: 0 }}>
          We transform ambitious ideas into scalable, AI-driven products that empower businesses and delight users.
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
          <a href="/contact" style={btn.primary}>Start a Project</a>
          <a href="#services" style={btn.ghost}>Explore Services</a>
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#4a5568', fontWeight: 500, marginTop: 10 }}>
          <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: '#2dd4bf', boxShadow: '0 0 8px #2dd4bf' }} />
          Available for new projects
        </div>
      </section>

      {/* Divider */}
      <div style={{ height: 1, background: 'linear-gradient(90deg,transparent,rgba(255,255,255,0.07),transparent)', margin: `0 ${px}` }} />

      {/* Stats */}
      <section ref={statsRef} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4,1fr)', gap: 0, margin: `32px ${px}`, borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)', opacity: 0, transform: 'translateY(20px)', transition: 'opacity 0.6s ease 0.1s, transform 0.6s ease 0.1s' }}>
        {stats.map((st, i) => (
          <div key={st.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: isMobile ? '22px 10px' : '32px 20px', gap: 6, borderRight: (!isMobile && i < 3) ? '1px solid rgba(255,255,255,0.06)' : 'none', borderBottom: (isMobile && i < 2) ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
            <span style={{ fontFamily: "'Playfair Display',serif", fontSize: isMobile ? '24px' : '34px', fontWeight: 700, color: '#2dd4bf', lineHeight: 1 }}>{st.value}</span>
            <span style={{ fontSize: isMobile ? '10px' : '12px', fontWeight: 500, color: '#4a5568', letterSpacing: '0.06em', textTransform: 'uppercase', textAlign: 'center' }}>{st.label}</span>
          </div>
        ))}
      </section>

      {/* Mission */}
      <section style={{ padding: `60px ${px} 40px` }} id="about">
        <div className="section-label">Our Mission</div>
        <div style={{ display: 'grid', gridTemplateColumns: isTablet ? '1fr' : '1fr 1fr', gap: isTablet ? '22px' : '48px', marginTop: 16, alignItems: 'start' }}>
          <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 'clamp(20px,3vw,36px)', fontWeight: 600, lineHeight: 1.25, color: '#f0f4f8', letterSpacing: '-0.01em', margin: 0 }}>
            Empowering businesses through intelligent technology
          </h2>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: '#8b9bb4', fontWeight: 300, margin: 0 }}>
            We strive to empower businesses and individuals by building smart applications that harness the potential of Artificial Intelligence, machine learning, and automation to create meaningful, lasting impact. Every line of code we write is guided by a commitment to quality, performance, and user experience.
          </p>
        </div>
      </section>

      {/* Services */}
      <section id="services" style={{ padding: `40px ${px} 60px` }}>
        <div className="section-label">What We Do</div>
        <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 'clamp(20px,3vw,32px)', fontWeight: 600, color: '#f0f4f8', marginBottom: 32, letterSpacing: '-0.01em' }}>
          Our core capabilities
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr 1fr' : 'repeat(4,1fr)', gap: 18 }}>
          {services.map((svc, i) => <ServiceCard key={svc.title} service={svc} delay={i * 80} />)}
        </div>
      </section>

      {/* CTA Banner */}
      <section style={{ margin: `0 ${px}`, borderRadius: 20, background: 'linear-gradient(135deg,rgba(45,212,191,0.08),rgba(129,140,248,0.08))', border: '1px solid rgba(45,212,191,0.15)', padding: isMobile ? '44px 20px' : '60px 40px', display: 'flex', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
          <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 'clamp(20px,3vw,36px)', fontWeight: 700, color: '#f0f4f8', letterSpacing: '-0.02em', margin: 0 }}>Ready to build something great?</h2>
          <p style={{ fontSize: 16, color: '#8b9bb4', fontWeight: 300, margin: 0 }}>Let's turn your vision into a reality.</p>
          <a href="/contact" style={btn.primary}>Get in Touch →</a>
        </div>
      </section>

    </main>
  );
};

const btn = {
  primary: { display: 'inline-block', padding: '12px 28px', background: 'linear-gradient(135deg,#2dd4bf,#818cf8)', color: '#fff', textDecoration: 'none', borderRadius: 10, fontWeight: 600, fontSize: 15, letterSpacing: '0.01em', boxShadow: '0 4px 24px rgba(45,212,191,0.25)' },
  ghost:   { display: 'inline-block', padding: '12px 28px', border: '1px solid rgba(255,255,255,0.12)', color: '#8b9bb4', textDecoration: 'none', borderRadius: 10, fontWeight: 500, fontSize: 15, background: 'rgba(255,255,255,0.03)' },
};

export default Home;